import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser Web Speech API dictation. Chromium-only (Chrome/Edge); degrades
 * gracefully elsewhere (`supported` is false). No server route — the audio
 * never leaves the browser. Whisper-backed cross-browser dictation is a
 * documented future upgrade.
 */
export interface SpeechDictation {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
};

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * onResult receives the best transcript built so far for the current session
 * (accumulated finals + the live interim), plus whether the latest chunk is final.
 */
export function useSpeechDictation(
  onResult: (text: string, isFinal: boolean) => void,
): SpeechDictation {
  const ctorRef = useRef<(new () => SpeechRecognitionLike) | null>(null);
  if (ctorRef.current === null) ctorRef.current = getSpeechRecognitionCtor();
  const supported = Boolean(ctorRef.current);

  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const finalRef = useRef('');
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = ctorRef.current;
    if (!Ctor) return;
    // Tear down any prior instance before starting fresh.
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    finalRef.current = '';
    rec.onresult = (event) => {
      let interim = '';
      let sawFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result) continue;
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalRef.current = `${finalRef.current} ${transcript}`.trim();
          sawFinal = true;
        } else {
          interim = `${interim} ${transcript}`.trim();
        }
      }
      const combined = `${finalRef.current} ${interim}`.trim();
      onResultRef.current(combined, sawFinal);
    };
    rec.onend = () => {
      setListening(false);
    };
    rec.onerror = () => {
      setListening(false);
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  useEffect(
    () => () => {
      const rec = recognitionRef.current;
      if (rec) {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
      }
    },
    [],
  );

  return { supported, listening, start, stop, toggle };
}
